import { ChangeEvent, memo, useCallback, useMemo, useRef, useState } from 'react';

import { Avatar, Badge, Dialog, Grid, IconButton, Input, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { PlusIcon } from '@chhjit/react-components';

import { ImageCropper } from '..';
import { useStyles } from './styles';
import { PickAvatarProps } from './types';

export const PickAvatar = memo(
  ({ avatarUrl, avatarApproved, onCropAvatarComplete, loading = false }: PickAvatarProps) => {
    const styles = useStyles();

    const inputAvatar = useRef<HTMLInputElement>();
    const [selectedAvatar, setSelectedAvatar] = useState<Blob>();
    const [selectedCroppedAvatar, setSelectedCroppedAvatar] = useState<Blob>();
    const [isOpenCropDialog, setIsOpenCropDialog] = useState(false);

    const avatarStatus = useMemo(() => {
      if (avatarUrl || selectedCroppedAvatar) {
        let status = 'Pending';

        if (typeof avatarApproved === 'boolean' && !selectedCroppedAvatar) {
          status = avatarApproved ? 'Approved' : 'Denied';
        }

        return status;
      }

      return undefined;
    }, [avatarApproved, avatarUrl, selectedCroppedAvatar]);

    const handleOpenCropDialog = useCallback(() => {
      setIsOpenCropDialog(true);
    }, [setIsOpenCropDialog]);

    const handleCloseCropDialog = useCallback(() => {
      setIsOpenCropDialog(false);
    }, [setIsOpenCropDialog]);

    const onCrop = useCallback(
      (croppedAvatar: Blob) => {
        setSelectedCroppedAvatar(croppedAvatar);
        onCropAvatarComplete(croppedAvatar);
        handleCloseCropDialog();
      },
      [handleCloseCropDialog, onCropAvatarComplete],
    );

    const onChangeSelectedFile = useCallback(
      async (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
          setSelectedAvatar(e.target.files[0]);
          handleOpenCropDialog();
        }
      },
      [setSelectedAvatar, handleOpenCropDialog],
    );

    // Show skeleton when loading
    if (loading) {
      return (
        <Grid container alignItems="center" direction="column">
          <Grid item>
            <Skeleton variant="circle" width={120} height={120} />
          </Grid>
        </Grid>
      );
    }

    return (
      <>
        <Dialog
          open={isOpenCropDialog}
          onClose={handleCloseCropDialog}
          disableBackdropClick
          classes={{ paper: styles.dialogPaper }}
        >
          {selectedAvatar && (
            <ImageCropper
              imageForCrop={selectedAvatar}
              onCropperComplete={onCrop}
              onClose={handleCloseCropDialog}
              aspect={1}
            />
          )}
        </Dialog>

        <Grid container alignItems="center" direction="column">
          <Grid item>
            <Input
              className={styles.inputAvatar}
              type="file"
              inputProps={{ accept: '.jpg,.png,.bmp' }}
              ref={inputAvatar}
              onChange={onChangeSelectedFile}
            />

            <IconButton
              className={styles.avatarIconButton}
              onClick={() => (inputAvatar.current?.firstChild as HTMLInputElement).click()}
            >
              <Badge
                badgeContent={
                  <div className={styles.addAvatarBadge}>
                    <PlusIcon />
                  </div>
                }
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <Avatar
                  classes={{ root: styles.avatar }}
                  src={selectedCroppedAvatar ? URL.createObjectURL(selectedCroppedAvatar) : avatarUrl}
                />
              </Badge>
            </IconButton>
          </Grid>

          {avatarStatus && (
            <Typography variant="h5" className={styles.avatarApproved}>
              {avatarStatus}
            </Typography>
          )}
        </Grid>
      </>
    );
  },
);
